import { skipToken } from '@apollo/client/react'
import { produce } from 'immer'
import { useCheckTokenForArgs } from 'src/utils/token'
import type {
  ExperienceLevel,
  Gender,
  PromptStyle,
  TSUBAKI_PROMPT_HELPER_VALUE,
  TSUBAKI2_PROMPT_HELPER_VALUE,
} from '../constants/preference'
import { cache } from './client'
import type {
  AgeVerificationStatus,
  GetMyPreferencesQuery,
} from './generated/graphql'
import {
  GetMyPreferencesDocument,
  SetPreferencesDocument,
} from './generated/graphql'
import {
  createFetchQueryHelper,
  createMutationHelper,
  createQueryHook,
  createRefetchQueryHelper,
  modifyQueryHook,
} from './utils'

export type UserPreferences = {
  showNsfw?: boolean
  blurNsfw?: boolean
  promoEmail?: boolean
  ageVerificationStatus?: AgeVerificationStatus
  experienceLevel?: ExperienceLevel
  disableAutoPromptHelper?: boolean
  tsubakiPromptHelper?: TSUBAKI_PROMPT_HELPER_VALUE
  tsubaki2PromptHelper?: TSUBAKI2_PROMPT_HELPER_VALUE
  lang?: string

  // onboarding user info
  gender?: Gender
  promptStyle?: PromptStyle
  interestedIn?: string[]
  favoritedCharacterTags?: string[]
  favoritedCharacterTacks?: string[]

  richTextTriggerWords?: boolean

  push?: boolean
} & {
  [K in `push-${string}`]?: boolean
}

const useGetMyPreferences = modifyQueryHook(
  createQueryHook(GetMyPreferencesDocument),
  {
    reduce: r => r?.me?.preferences,
  },
)
export const useMyPreferences = (
  options?: Parameters<typeof useGetMyPreferences>[1],
) => {
  const input = useCheckTokenForArgs({})
  const res = useGetMyPreferences(input ?? skipToken, options)
  return res as Omit<typeof res, 'data'> & { data: UserPreferences }
}
export const fetchGetMyPreferences = createFetchQueryHelper(
  GetMyPreferencesDocument,
)
export const refetchGetMyPreferences = createRefetchQueryHelper(
  GetMyPreferencesDocument,
)
export const updateGetMyPreferences = (
  newPreferences?: Record<string, unknown>,
) => {
  cache.updateQuery(
    { query: GetMyPreferencesDocument },
    produce((data: GetMyPreferencesQuery | null) => {
      const me = data?.me
      if (me == null) {
        return
      }
      me.preferences = { ...me.preferences, ...newPreferences }
    }),
  )
}

export const [useSetPreferences, executeSetPreferences] = createMutationHelper(
  SetPreferencesDocument,
)
